/* eslint-disable @typescript-eslint/naming-convention */
import { parseUrl, stringifyQuery } from '@tencent/halo-utils';
import md5 from 'md5';
import { FAKE_UA } from './const';
import { sourceAjax } from './sourceAjax';

export * from './const';

/** 无需传入，由 SDK 计算的参数 */
export interface PresetData {
  /**
   * 点击ID，根据 gaid 和当前时间生成
   * @example
   * ```js
   * md5(`${gaid}-${Date.now()}-${Math.random()}`);
   * ```
   */
  clickId: string;
}

export interface OfferInfo {
  /** 广告单号 */
  offer_id: string;
  /** Google Advertising ID，Google 推广ID */
  gaid: string;
  /** 市场渠道号 ID */
  supplyId: string;
  /**
   * 追踪链接，归因链接，attribution link
   * @description 每个 Offer 都有一个带参数链接，需要在此基础上拼接参数上报归因
   */
  s2s_tracking_link: string;
  /**
   * @deprecated
   * 同 s2s_tracking_link，已废弃
   */
  tracking_link?: string;
  /** 包名 */
  packageName: string;
  /** 地区（缩写，用,分割，如 US,UK) */
  region: string;
  /** 上报类型 s2s/c2s，默认为 c2s */
  report_type?: string;
  /** 网盟名称 */
  firm: string;
  /** 手游助手市场端的链路追踪 ID */
  trace_id?: string;
  /**
   * 资源位ID, 目前会覆盖 af_siteid
   * @example
   * ```js
   * alg_inter.match(/respos=(\d*)/)[1];
   * ```
   */
  resPos: string;
  /** IP 地址 */
  ip?: string;
  /** Global User Id */
  guid?: string;
  /** 动态包 ID */
  dynamicPkgId?: string;
  /** 市场版本号 */
  marketVersion?: string;
  /** 系统名称 */
  os?: string;
  /** 系统版本号 */
  osv?: string;
  /** 媒体来源，一般是 _int 结尾的固定字符串，如 gameloop_int */
  pid?: string;
  /** (appsflyer) 广告活动 ID */
  c_ad_activity?: string;
  /** (appsflyer) 广告平台发行商 ID */
  af_siteid?: string;
  /** (appsflyer) 将用户重定向到两个平台（Android 和 iOS）的指定 URL */
  af_r?: string;
}

export interface GAIDReportData extends OfferInfo, PresetData { }

/** 已接入的广告联盟名称 */
export enum AffiliateNetworks {
  appsflyer = 'appsflyer',
  tecdo = 'tecdo',
  appnext = 'appnext',
  adjust = 'adjust',
  mobvista = 'mobvista',
  // 下面两个平台目前使用 s2s 回传
  // yeahmobi = 'yeahmobi',
}

export function getFullParams(params: OfferInfo): GAIDReportData {
  return {
    ...params,
    clickId: md5(`${params.gaid}-${Date.now()}-${Math.random()}`),
  };
}

/**
 * 点击归因上报
 * @description
 * 上报点击到 appsflyer 平台，用户自行下载 App 安装后打开时，appsflyer sdk 上报激活，完成点击归因。
 * 这种方式分为 c2s（客户端上报） 和 s2s （服务端上报）两种。
 */
export function reportClickAttribution(params: OfferInfo) {
  try {
    const fullParams = getFullParams(params);

    if (params.report_type === 's2s') return reportServer(fullParams);
    return reportClient(fullParams);
  } catch (e) {
    window.aegis?.report(`Error:gameloop-ad-tracker|reportClickAttribution|${params.region}|${params.firm}|${params.offer_id}|${params.gaid}`, e);
  }
}

/**
 * 点击归因重定向（应用商店，如 google play）
 * @description
 * 原理是打开一个平台提供的 tracking url 完成上报，然后被 302 跳转到应用商店完成下载。
 * 平台提供的 tracking url 一般都会提供 302 跳转的功能，所以属于 c2s 上报。
 */
export function reportAndRedirect(params: OfferInfo) {
  try {
    const fullParams = getFullParams(params);

    const link = generateC2SReportLink(fullParams);
    const a = document.createElement('a');
    const id = 'reportAndRedirect';
    a.setAttribute('href', link);
    a.setAttribute('target', '_blank');
    a.setAttribute('id', id);

    // 防止反复添加
    if (!document.getElementById(id)) {
      document.body.appendChild(a);
    }

    a.click();
  } catch (e) {
    window.aegis?.report(`Error:gameloop-ad-tracker|reportAndRedirect|${params.region}|${params.firm}|${params.offer_id}|${params.gaid}`, e);
  }
}

/** 生成c2s点击归因上报信息 */
export function generateC2SReportLink(params: GAIDReportData) {
  if (!params.s2s_tracking_link && !params.tracking_link) {
    throw new Error('Params Error: One of "s2s_tracking_link" or "tracking_link" is required!');
  }

  const urlParsed = parseUrl(params.s2s_tracking_link || params.tracking_link!, true);
  const newParams = urlParamsGenerators[params.firm](params);

  // 合并 tracking link 上自带的参数
  urlParsed.set('query', { ...urlParsed.query, ...newParams });

  // toString 时会自动对 url 参数进行 encode
  return urlParsed.toString()
    .replace('&%7B', '&{') // 对于 url 参数中带有通配符 {} 的情况，不进行 encode
    .replace('%7D=', '}=');
}

/** c2s 客户端上报 */
export function reportClient(params: GAIDReportData) {
  const reportUrl = generateC2SReportLink(params);
  window.aegis?.report(`DEBUG:gameloop-ad-tracker|reportClient|${params.region}|${params.firm}|${params.offer_id}|${params.gaid}|${reportUrl}`);

  new Image().src = reportUrl;
}


/** s2s 服务端上报 */
export async function reportServer(params: GAIDReportData) {
  const { s2s_tracking_link, gaid, ...otherParams } = params;

  const reportInfo = {
    head: {
      userInfo: {
        guid: params.guid,
      },
    },
    listS: {
      region: [params.region],
      pkg: [params.packageName],
      os: [params.os],
      osv: [params.osv],
      ifa: [params.gaid],
      ua: [FAKE_UA],
      offerId: [params.offer_id],
      // FIXME: 为了在 appsflyer dashboard 方便看到资源位信息，这里临时传资源位ID，未来可以改成场景、资源位名称等更具辨识度的siteid
      af_siteid: [params.resPos],
      c_ad_activity: [params.c_ad_activity],
      sub_affiliate_id: [params.supplyId],
      aff_sub1: [params.clickId],
      site_name: [params.resPos],
      aff_sub2: [params.guid],
      aff_sub3: [encodeURIComponent(stringifyQuery(otherParams))],
      aff_sub4: [params.dynamicPkgId],
    },
    OperateId: 'AdReport',
  };

  return sourceAjax(reportInfo);
}

/**
 * 链接参数生成表
 * @description
 * 根据不同网盟的归因链接协议转换链接上参数
 */
export const urlParamsGenerators: Record<string, (params: GAIDReportData) => Record<string, string>> = {};

urlParamsGenerators.appsflyer = (params) => {
  const { s2s_tracking_link, gaid, ...otherParams } = params;

  return {
    clickid: params.clickId,
    advertising_id: params.gaid,
    af_c_id: params.offer_id,
    // FIXME: 为了在 appsflyer dashboard 方便看到资源位信息，这里临时传资源位ID，未来可以改成场景、资源位名称等更具辨识度的siteid
    af_siteid: params.resPos || params.af_siteid || '',
    pid: params.pid || 'gameloop_int',
    c: params.c_ad_activity || '',
    af_sub_siteid: params.supplyId || '',
    af_ad_id: params.resPos || '',
    af_sub2: params.guid || '',
    // 塞进所有其他 postback 参数
    af_sub3: stringifyQuery(otherParams) || '',
    af_sub4: params.dynamicPkgId || '',
    af_r: params.af_r || '',
    af_click_lookback: '7d',
  };
};

urlParamsGenerators.tecdo = (params) => {
  const { s2s_tracking_link, gaid, ...otherParams } = params;

  return {
    // 如果后台下发的 offer_id 形如 tecdo_172668890，需要去掉前面的 firm 字段
    offer_id: params.offer_id.split('_').length === 1 ? params.offer_id : params.offer_id.split('_')[1],
    gaid,
    '{aff_sub2}': params.clickId,
    '{aff_sub5}': params.supplyId || '',
    // 塞进所有其他 postback 参数
    '{aff_sub3}': stringifyQuery(otherParams),
  };
};

urlParamsGenerators.appnext = (params) => {
  const { s2s_tracking_link, gaid, ...otherParams } = params;

  return {
    d: gaid,
    r: params.resPos || params.af_siteid || '',
    subid: params.supplyId || '',
    // 塞进所有其他 postback 参数
    q: stringifyQuery(otherParams),
  };
};

urlParamsGenerators.adjust = (params) => {
  const { gaid } = params;

  return {
    gps_adid: gaid,
  };
};

urlParamsGenerators.mobvista = (params) => {
  const { s2s_tracking_link, gaid, ...otherParams } = params;
  if (!params.ip) {
    throw new Error('Error: param "ip" is required in mobvista!');
  }

  return {
    mb_subid: params.supplyId,
    aff_sub: params.clickId,
    mb_ip: params.ip,
    mb_ua: encodeURIComponent(window.navigator.userAgent),
    mb_gaid: params.gaid,
    site_name: params.resPos || '',
    aff_sub2: params.guid || 'web',
    aff_sub3: encodeURIComponent(stringifyQuery(otherParams)),
    aff_sub4: params.dynamicPkgId || '',
  };
};
